import {Modal} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {RadioButton} from '@packages/ui';
import type {GetServerSideProps} from 'next';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import type {GetServerSidePropsResult} from 'next/types';
import {parseCookies} from 'nookies';
import {useEffect, useState} from 'react';
import {MdCheckCircle} from 'react-icons/md';
import {z} from 'zod';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  BOX_PAGE_ADVISER_DETAIL,
  BOX_PAGE_QUESTION_ADD,
  type QuestionFormStatus,
  questionFormStatus,
} from '../../../core/entities/box/constants';
import {
  getAdviser,
  postQuestion,
} from '../../../core/repositories/boxRepository';
import type {
  ListedAdviserType,
  PostQuestionResponseType,
} from '../../../core/repositories/boxRepositoryTypes';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
  getUsersMe,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {AdviserList} from '../../../features/box/components/AdviserList';
import {CareerLabel} from '../../../features/box/components/CareerLabel';
import Layout from '../../../features/box/components/Layout';
import {Navigation} from '../../../features/box/components/Navigation';
import {
  QuestionForm,
  QuestionFormInput,
} from '../../../features/box/components/QuestionForm';
import {createBoxPath} from '../../../features/box/usecases/routes';
import {withHandler} from '../../../server/token/withHandler';
import {CommonLink} from '../../../shared/components/CommonLink';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {modalConfig} from '../../../shared/components/modalConfig';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {UnexpectedError} from '../../../utils/unexpectedError';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  loggedinAdviserName: string;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  selectedAdviserByParam?: ListedAdviserType;
  metaInfo: {
    title: string;
    currentUrl: string;
  };
};

const QueryParameter = z.object({
  adviserName: z.string().optional(),
});
type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    let loggedinAdviserName = '';
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (isValid) {
      const usersMeResponse = await getUsersMe({cookies});
      if (!usersMeResponse.ok) {
        throw new UnexpectedError();
      }

      loggedinAdviserName = usersMeResponse.value.adviserUserName;
    }

    const {adviserName} = context.validatedQuery;

    const path = context.req.url;
    if (!path) {
      throw new UnexpectedError();
    }
    // クエリパラメータを除去してcanonical URLを生成
    const urlWithoutQuery = path.split('?')[0];
    const metaInfo = {
      title: `質問する${TITLE_SUFFIX}`,
      currentUrl: `${GSSKT_HOST}${urlWithoutQuery}`,
    };

    if (!adviserName) {
      return {
        props: {loggedinAdviserName, metaInfo},
      };
    }

    const adviserResponse = await getAdviser({cookies, adviserName});
    if (!adviserResponse.ok) {
      return {
        props: {loggedinAdviserName, metaInfo},
      };
    }

    const props: Props = {
      loggedinAdviserName,
      selectedAdviserByParam: adviserResponse.value,
      metaInfo,
    };
    if (!isValid) return {props};

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const AddQuestion: NextPageWithLayout<Props> = ({
  loggedinAdviserName,
  userDataForAnalytics,
  selectedAdviserByParam,
  metaInfo,
}) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();

  const cookies = parseCookies();

  const [selectedAdviser, setSelectedAdviser] = useState<
    ListedAdviserType | undefined
  >(selectedAdviserByParam);
  const [isAdviserListShown, setIsAdviserListShown] = useState<boolean>(false);
  const [status, setStatus] = useState<QuestionFormStatus>(
    questionFormStatus.add,
  );
  const [questionBody, setQuestionBody] = useState<string>('');

  const selectAdviser = (adviser: ListedAdviserType) => {
    setSelectedAdviser(adviser);
    setIsAdviserListShown(false);
  };

  const form = useForm<QuestionFormInput>({
    initialValues: {
      question: '',
    },
    validate: zodResolver(QuestionFormInput),
  });

  const onSubmit = async (
    input: QuestionFormInput,
  ): Promise<{
    status: QuestionFormStatus;
    res?: PostQuestionResponseType;
  }> => {
    if (status === questionFormStatus.add) {
      setQuestionBody(input.question);
      setStatus(questionFormStatus.confirm);
      return {status};
    }

    const res = await postQuestion({
      cookies,
      data: {
        body: input.question,
        isOpenQuestion: !selectedAdviser,
        hashTags: [],
        adviserIDs: selectedAdviser ? [selectedAdviser.id] : [],
      },
    });
    if (!res.ok) {
      alert('エラーが発生しました');
      return {status};
    }

    setStatus(questionFormStatus.done);
    return {
      status,
      res: res.value,
    };
  };

  const onError = () => {
    // TODO: error handling
  };

  const resetForm = () => {
    form.reset();
    setSelectedAdviser(undefined);
    setQuestionBody('');
    setStatus(questionFormStatus.add);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedAdviser の変化のみをトリガーにしたいため、他の参照値は意図的に依存配列から除外
  useEffect(() => {
    if (isWebView) return;
    const nextPath = createBoxPath({
      page: BOX_PAGE_QUESTION_ADD,
      ...(selectedAdviser && {adviserName: selectedAdviser.userName}),
    });
    // 現在のURLと同じ場合は不要な router.replace をスキップ
    if (router.asPath === nextPath) return;
    void router.replace(nextPath);
  }, [selectedAdviser]);

  return (
    <>
      <Head>
        <title key="title">{metaInfo.title}</title>
        <meta key="og:title" property="og:title" content={metaInfo.title} />
        <meta key="og:url" property="og:url" content={metaInfo.currentUrl} />
        <link rel="canonical" href={metaInfo.currentUrl} key="canonical" />
      </Head>
      <div className="flex min-h-screen items-start justify-center bg-background-1 pb-64 pc:py-32">
        <div className="w-full max-w-[800px] bg-white py-32 pc:px-32 ">
          <h1
            className={`${
              status === questionFormStatus.add ? '' : 'hidden'
            }  px-16 text-lg font-bold leading text-black-1 pc:px-0`}
          >
            匿名で質問する
          </h1>
          {status !== questionFormStatus.add && (
            <>
              <p className="text-center text-lg font-bold leading text-black-1">
                この内容で送信します。 <br />
                よろしいですか？
              </p>
              <p className="mt-8 text-center text leading text-black-1">
                投稿後の変更はできません。
              </p>
            </>
          )}
          <div
            className={`${
              status === questionFormStatus.add ? 'mt-32' : 'mt-16'
            }`}
          >
            {status === questionFormStatus.add && (
              <div className="px-16 pc:px-0">
                <button
                  className="mr-20 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
                  type="button"
                  onClick={() => {
                    setSelectedAdviser(undefined);
                  }}
                >
                  <div className="flex">
                    <RadioButton isSelected={!selectedAdviser} />
                    <p className="text leading">回答者を指名しない</p>
                  </div>
                </button>
                <button
                  className="pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
                  type="button"
                  onClick={() => {
                    setIsAdviserListShown(true);
                  }}
                >
                  <div className="flex">
                    <RadioButton isSelected={Boolean(selectedAdviser)} />
                    <p className="text leading">回答者を指名する</p>
                  </div>
                </button>
              </div>
            )}

            {selectedAdviser && (
              <>
                <div className="mt-16 flex items-start justify-between border-b-2 border-dashed border-white bg-background-3 px-16 py-8">
                  <div className="font-bold text-black-1">指名した回答者</div>
                  <button
                    className="ml-16 text leading text-link-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
                    onClick={() => {
                      setIsAdviserListShown(true);
                    }}
                  >
                    変更する
                  </button>
                </div>
                <div className=" flex items-start justify-between bg-background-3 p-16">
                  <div className="w-[40px]">
                    <CommonLink
                      href={createBoxPath({
                        page: BOX_PAGE_ADVISER_DETAIL,
                        adviserName: selectedAdviser.userName,
                      })}
                      className={
                        'mb-8 overflow-hidden rounded-full pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out '
                      }
                    >
                      <Image
                        src={
                          selectedAdviser.iconImageURL
                            ? selectedAdviser.iconImageURL
                            : '/next-image/box/default.svg'
                        }
                        width={40}
                        height={40}
                        alt={
                          selectedAdviser.displayName
                            ? selectedAdviser.displayName
                            : '相談室回答者'
                        }
                      />
                    </CommonLink>
                    <CareerLabel isStudent={selectedAdviser.isStudent} />
                  </div>
                  <div className="w-[calc(100%_-_48px)]">
                    <div className="flex justify-between">
                      <CommonLink
                        href={createBoxPath({
                          page: BOX_PAGE_ADVISER_DETAIL,
                          adviserName: selectedAdviser.userName,
                        })}
                        className={
                          'line-clamp-1 w-[calc(100%_-_72px)] text-left text leading text-black-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
                        }
                      >
                        {selectedAdviser.displayName
                          ? selectedAdviser.displayName
                          : '相談室回答者'}
                      </CommonLink>
                    </div>

                    <div className="mt-8 max-h-[70px] bg-background-1 p-12">
                      <p className=" line-clamp-3 text-xs text-black-2">
                        {selectedAdviser.introduction}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="px-16 pc:px-0">
              <QuestionForm
                status={status}
                isAdviserSelected={Boolean(selectedAdviser)}
                questionBody={questionBody}
                setStatus={setStatus}
                onSubmit={onSubmit}
                onError={onError}
                form={form}
              />
              {status === questionFormStatus.add && (
                <div className="mt-24 bg-background-1 p-16">
                  <p className="text leading text-black-1">
                    ✓ 誤字脱字がないかご確認を!
                    <br />✓
                    誹謗中傷や公序良俗に反する内容の場合、運営により削除される場合があります
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        opened={status === questionFormStatus.add && isAdviserListShown}
        onClose={() => {
          setIsAdviserListShown(false);
        }}
        size={348}
        {...modalConfig}
      >
        <AdviserList selectAdviser={selectAdviser} />
      </Modal>

      <Modal
        opened={status === questionFormStatus.done}
        onClose={resetForm}
        size={327}
        {...modalConfig}
      >
        <div className="px-16 py-24">
          <p className="text-center text-lg font-bold leading text-black-1">
            質問を送信しました！
          </p>
          <div className="my-24 flex justify-center">
            <svg width="0" height="0">
              <linearGradient
                id="blue-gradient"
                x1="100%"
                y1="100%"
                x2="100%"
                y2="0%"
              >
                <stop stopColor="#4F9DC5" offset="0%" />
                <stop stopColor="#1E6A93" offset="100%" />
              </linearGradient>
            </svg>
            <MdCheckCircle style={{fill: 'url(#blue-gradient)'}} size={56} />
          </div>
          <p className="text-center text leading text-black-1">
            回答者の方へ質問が送信されました。
            <br />
            回答が届くまで、しばらくお待ちください。
          </p>

          <div className="mt-24 flex justify-center">
            <button
              className="text leading text-primary-1 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
              type="button"
              onClick={() => {
                form.reset();
                setSelectedAdviser(undefined);
                setQuestionBody('');
                setStatus(questionFormStatus.add);
              }}
            >
              戻る
            </button>
          </div>
        </div>
      </Modal>

      <Navigation loggedinAdviserName={loggedinAdviserName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default AddQuestion;

AddQuestion.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};
